import { Box, Button, Heading, HStack, Text, VStack } from '@chakra-ui/react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { MdOutlineWebhook } from 'react-icons/md';
import { useGetJobWebhookConfigQuery, useModalitiesQuery, useUpdateJobWebhookConfigMutation } from '@/app/services/nucleus';
import { setNotificationLogMessage } from '@/features/notification/notificationSlice';
import LoaderOverlay from '../common/LoaderOverlay';
import AlertComponent from '../common/AlertComponent';
import JobFeedFormFields from './JobFeedFormFields';
import { idsToModalityOptions, isValidHttpsUrl, jobFeedErrorMessage, modalitiesToOptions } from './jobFeedHelpers';

const EditJobFeed = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const initialRef = useRef(null);

  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [modalities, setModalities] = useState([]);
  const [nameError, setNameError] = useState(false);
  const [urlError, setUrlError] = useState(false);

  const { data: config, isLoading: isFetching, isError: isFetchError, error: fetchError } = useGetJobWebhookConfigQuery(id);
  const { data: modalitiesData } = useModalitiesQuery();
  const [updateConfig, { isLoading: isSubmitting, isError: isUpdateError, error: updateError, reset }] = useUpdateJobWebhookConfigMutation();

  const modalityOptions = useMemo(() => modalitiesToOptions(modalitiesData?.items || []), [modalitiesData?.items]);

  // Pre-populate form once both the config and modality labels are available.
  useEffect(() => {
    if (config) {
      setName(config.name ?? '');
      setUrl(config.url ?? '');
      setModalities(idsToModalityOptions(config.modality_ids || [], modalitiesData?.items || []));
    }
  }, [config, modalitiesData?.items]);

  const handleSubmit = (e) => {
    e.preventDefault();
    let hasError = false;
    if (!name.trim()) {
      setNameError(true);
      hasError = true;
    }
    if (!isValidHttpsUrl(url)) {
      setUrlError(true);
      hasError = true;
    }
    if (hasError) return;
    reset();

    updateConfig({
      id,
      name: name.trim(),
      url: url.trim(),
      modality_ids: modalities.map((m) => m.value),
    })
      .unwrap()
      .then(() => {
        dispatch(setNotificationLogMessage({ msg: 'Job feed updated successfully', status: 'success' }));
        navigate('/job_feed');
      })
      .catch((err) => console.error('Error updating job feed config', err));
  };

  return (
    <VStack spacing={4} alignItems="left">
      <Box as="header">
        <Heading as="h4" size="md" fontWeight="bold" color="brand.900">
          <HStack>
            <MdOutlineWebhook />
            <Text>Edit Job Feed</Text>
          </HStack>
        </Heading>
      </Box>
      {isFetchError && <AlertComponent status="error" title="Error fetching job feed" description={jobFeedErrorMessage(fetchError, 'Could not load job feed')} />}
      {!isFetching && !isFetchError && (
        <Box position="relative" width={{ base: '100%', md: '70%' }}>
          <form id="edit_job_feed" onSubmit={handleSubmit}>
            <VStack spacing={4} position="relative">
              <LoaderOverlay loading={isSubmitting} />
              {isUpdateError && <AlertComponent status="error" title="Error updating job feed" description={jobFeedErrorMessage(updateError)} />}
              <JobFeedFormFields
                initialRef={initialRef}
                name={name}
                onNameChange={(v) => {
                  setName(v);
                  setNameError(false);
                }}
                nameError={nameError}
                url={url}
                onUrlChange={(v) => {
                  setUrl(v);
                  setUrlError(false);
                }}
                urlError={urlError}
                modalities={modalities}
                onModalitiesChange={setModalities}
                modalityOptions={modalityOptions}
              />
              <HStack width="100%" py={3} justifyContent="end">
                <Button onClick={() => navigate('/job_feed')}>Cancel</Button>
                <Button type="submit" variant="purple">
                  Save
                </Button>
              </HStack>
            </VStack>
          </form>
        </Box>
      )}
    </VStack>
  );
};

export default EditJobFeed;
